import { isAllowed } from '@opsbase/lib-form/FormConstraints';
import QT from '@opsbase/lib-form/FormQuestionType';
import doNothing from '@opsbase/lib-general/utils/doNothing';
import i18n from '@opsbase/lib-i18n';
import useOrgTemplateUpdate from '@opsbase/react-gql-hooks/orgtemplate/useOrgTemplateUpdate';
import useQuestionSets from '@opsbase/react-gql-hooks/questionset/useQuestionSets';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import useCss from '@opsbase/web-css/useCss/useCss';
import QuestionSetChooser from '@opsbase/web-data-choosers/QuestionSetChooser';
import Horizontal from '@opsbase/web-layout/Horizontal/Horizontal';
import Vertical from '@opsbase/web-layout/Vertical';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Accordion from '@opsbase/web-ui/Accordion';
import AccordionDetails from '@opsbase/web-ui/AccordionDetails';
import AccordionSummary from '@opsbase/web-ui/AccordionSummary';
import Text from '@opsbase/web-ui/Text';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QuestionConstraints from './QuestionConstraints';
const makeMyClasses = () => ({
    root: {
        '& .MuiExpansionPanel-root': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            marginTop: '0 !important',
        },
        '& .MuiCollapse-container.MuiCollapse-entered': {
            overflow: 'scroll',
        },
    },
});
export function OrgTemplateEditConstraintsTab(props) {
    var _a, _b;
    const { template: { id, formConstraints: fc }, } = props;
    const myCss = useCss(makeMyClasses);
    const updateOrgTemplate = useOrgTemplateUpdate();
    const [selectedAccordian, setSelectedAccordian] = useState('root');
    const [rootC, setRootC] = useState(fc.root || { types: undefined });
    const [sectC, setSectC] = useState(fc[QT.SECTION] || { types: undefined });
    const [repC, setRepC] = useState(fc[QT.REPETITION] || { types: undefined });
    const [qSetC, setQSetC] = useState(fc[QT.QUESTIONSET] || { ids: undefined });
    const secInRoot = isAllowed(rootC, QT.SECTION);
    const repInRoot = isAllowed(rootC, QT.REPETITION);
    const qsetInRoot = isAllowed(rootC, QT.QUESTIONSET);
    const hasRepetition = repInRoot || (secInRoot && isAllowed(sectC, QT.REPETITION));
    const hasSection = secInRoot || (repInRoot && isAllowed(repC, QT.SECTION));
    const hasQSet = qsetInRoot ||
        (hasSection && isAllowed(sectC, QT.QUESTIONSET)) ||
        (hasRepetition && isAllowed(repC, QT.QUESTIONSET));
    const { data: qSets } = useQuestionSets({
        variables: { id: qSetC === null || qSetC === void 0 ? void 0 : qSetC.ids },
        skip: !((_a = qSetC === null || qSetC === void 0 ? void 0 : qSetC.ids) === null || _a === void 0 ? void 0 : _a.length),
    });
    const _updateChange = useMemo(() => debounce(patch => {
        updateOrgTemplate({
            id: id,
            patch: { formConstraints: { ...fc, ...patch } },
        }).catch(doNothing);
    }, 1000, { leading: false, trailing: true }), [id, fc, updateOrgTemplate]);
    const _doChange = useMemo(() => patch => {
        if (patch.root)
            setRootC(patch.root);
        if (patch[QT.SECTION])
            setSectC(patch[QT.SECTION]);
        if (patch[QT.REPETITION])
            setRepC(patch[QT.REPETITION]);
        if (patch[QT.QUESTIONSET])
            setQSetC(patch[QT.QUESTIONSET]);
        _updateChange(patch);
    }, [_updateChange]);
    const handleOpen = useCallforward(v => () => setSelectedAccordian(v), []);
    const handleAllQSets = useCallback(() => _doChange({ [QT.QUESTIONSET]: { ids: undefined } }), [_doChange]);
    const handleChangeQSets = useCallback(v => _doChange({ [QT.QUESTIONSET]: { ids: v.map(i => i.id) } }), [_doChange]);
    const handleChangeRootC = useCallback(v => _doChange({ root: v }), [_doChange]);
    const handleChangeSectC = useCallback(v => _doChange({ [QT.SECTION]: v }), [_doChange]);
    const handleChangeRepC = useCallback(v => _doChange({ [QT.REPETITION]: v }), [_doChange]);
    const handleChangeMin = useCallback(min => {
        if (min !== undefined && min < 1)
            min = undefined;
        if (min > rootC.max) {
            _doChange({ root: { ...rootC, max: undefined, min } });
        }
        else {
            _doChange({ root: { ...rootC, min } });
        }
    }, [rootC, _doChange]);
    const handleChangeMax = useCallback(max => {
        if (max !== undefined && max < 1)
            max = undefined;
        if (max < rootC.min) {
            _doChange({ root: { ...rootC, min: undefined, max } });
        }
        else {
            _doChange({ root: { ...rootC, max } });
        }
    }, [rootC, _doChange]);
    const [maxTextError, setMaxTextError] = useState('');
    const maxTextField = useRef();
    useEffect(() => setMaxTextError(''), []);
    const validateMax = useCallback(() => {
        var _a;
        if (rootC.min) {
            const max = parseInt((_a = maxTextField.current) === null || _a === void 0 ? void 0 : _a.value);
            if (max < rootC.min)
                setMaxTextError(i18n.t('error-invalid-numeric-value-above', { min: rootC.min }));
            else
                setMaxTextError('');
        }
        else
            setMaxTextError('');
    }, [rootC]);
    const [minTextError, setMinTextError] = useState('');
    const minTextField = useRef();
    useEffect(() => setMinTextError(''), []);
    const validateMin = useCallback(() => {
        var _a;
        if (rootC.max) {
            const min = parseInt((_a = minTextField.current) === null || _a === void 0 ? void 0 : _a.value);
            if (min > rootC.max)
                setMinTextError(i18n.t('error-invalid-numeric-value-below', { max: rootC.max }));
            else
                setMinTextError('');
        }
        else
            setMinTextError('');
    }, [rootC]);
    return (React.createElement(Vertical, { padding: true, gutter: true, classes: { root: myCss.root }, shrink: true },
        React.createElement(ConstraintAccordian, { expanded: selectedAccordian === 'root', onChange: handleOpen('root'), summary: i18n.t('root-constraints'), help: i18n.t('root-constraints-help'), children: React.createElement(Vertical, { gutter: true, padding: true, style: { maxWidth: 1000, margin: 'auto' } },
                React.createElement(QuestionConstraints, { questionConstraint: rootC, onChange: handleChangeRootC }),
                React.createElement(Horizontal, { centered: true, gutter: true, justify: "flex-start" },
                    React.createElement(Vertical, null,
                        React.createElement(Text, null, i18n.t('min-quest-root')),
                        React.createElement(TextFieldWithStateAndBlur, { noMargin: true, error: minTextError, inputRef: minTextField, type: "number", inputProps: { min: 1 }, InputProps: { style: { width: 80 } }, value: rootC.min, onBlur: handleChangeMin, onTextInput: validateMin }))),
                React.createElement(Horizontal, { centered: true, gutter: true, justify: "flex-start" },
                    React.createElement(Vertical, null,
                        React.createElement(Text, null, i18n.t('max-quest-root')),
                        React.createElement(TextFieldWithStateAndBlur, { noMargin: true, error: maxTextError, inputRef: maxTextField, type: "number", inputProps: { min: 1 }, InputProps: { style: { width: 80 } }, value: rootC.max, onBlur: handleChangeMax, onTextInput: validateMax })))) }),
        hasSection && (React.createElement(ConstraintAccordian, { expanded: selectedAccordian === 'section', onChange: handleOpen('section'), summary: i18n.t('section-constraints'), help: i18n.t('section-constraints-help'), children: React.createElement(QuestionConstraints, { questionConstraint: sectC, onChange: handleChangeSectC }) })),
        hasRepetition && (React.createElement(ConstraintAccordian, { expanded: selectedAccordian === 'repetition', onChange: handleOpen('repetition'), summary: i18n.t('repetition-constraints'), help: i18n.t('repetition-constraints-help'), children: React.createElement(QuestionConstraints, { questionConstraint: repC, onChange: handleChangeRepC }) })),
        hasQSet && (React.createElement(ConstraintAccordian, { expanded: selectedAccordian === 'questionset', onChange: handleOpen('questionset'), summary: i18n.t('questionset-constraints'), help: i18n.t('questionset-constraints-help'), children: React.createElement(QuestionSetChooser, { multi: true, required: true, allSelectable: true, allSelected: !((_b = qSetC === null || qSetC === void 0 ? void 0 : qSetC.ids) === null || _b === void 0 ? void 0 : _b.length), allSelectedLabel: i18n.t('any-question'), onSelectAll: handleAllQSets, value: qSets || [], onChange: handleChangeQSets }) }))));
}
export default OrgTemplateEditConstraintsTab;
function ConstraintAccordian(props) {
    const { expanded, onChange, summary, help, children } = props;
    return (React.createElement(Accordion, { expanded: expanded, onChange: onChange },
        React.createElement(AccordionSummary, null,
            React.createElement(Text, { variant: "h1" }, summary)),
        React.createElement(AccordionDetails, null,
            React.createElement(Vertical, { gutter: true },
                React.createElement(Text, null, help),
                children))));
}
